import { AppBarStyles } from '@client/style/style.constants';
import { Box, type SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { guards } from '@shared/helpers/typeguards.helpers';
import { useMemo, useRef } from 'react';
import type { ComponentProps, ReactElement } from 'react';
import { makeStyles } from 'tss-react/mui';
import { MainLayoutScrollbars } from './MainLayoutScrollbars';
import { SidePanel } from './panels/SidePanel';

/** Whether the layout leaves room for the AppBar above it. */
type AppBarSpace = 'offset' | 'none';

const printStyle = {
  height: 'auto',
  marginTop: 0,
  overflow: 'visible',
} as const;

const useStyles = makeStyles<{ appBarSpace: AppBarSpace }>()(
  (theme, { appBarSpace }) => ({
    root: {
      display: 'grid',
      width: '100%',
      gridTemplateRows: '1fr',
      // Offset places content under the app bar, on the remaining height
      ...(appBarSpace === 'offset'
        ? {
            height: `calc(100vh - ${AppBarStyles.HEIGHT}px)`,
            marginTop: AppBarStyles.HEIGHT,
          }
        : { height: '100vh' }),
      '@media print': printStyle,
    },
    sidePanelLeft: {
      // Keep panel contents above full-viewport map overlays (z-index 2).
      position: 'relative',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    },
    sidePanelRight: {
      position: 'relative',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      borderLeft: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    },
  }),
);

const useContentStyles = makeStyles()(() => ({
  contentWrapper: {
    position: 'relative',
    // `min-width: 0` lets the `1fr` grid track shrink below its content's
    // intrinsic size. Without it a wide table (e.g. csv-import) forces the track
    // to grow past its share, overlapping the right side panel and stretching the
    // centered proposals bar.
    minWidth: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    minWidth: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden auto',
    scrollbarWidth: 'none',
    // The global theme gives every WebKit scrollbar a 9px track. Merely
    // setting `display: none` is not reliable in every Chromium/WebKit build,
    // and can leave that native thumb underneath our painted fade scrollbar.
    // Collapse both axes as well so the custom rail overlays the content
    // without reserving a scrollbar column.
    '&::-webkit-scrollbar': {
      display: 'none',
      width: 0,
      height: 0,
    },
    '@media print': {
      height: 'auto',
      overflow: 'visible',
    },
  },
}));

/**
 * The main scroll container. Native scrollbars are hidden so sticky headers
 * keep full width; {@link MainLayoutScrollbars} paints custom ones on top.
 */
export const MainContent: React.FC<{
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ children, sx }) => {
  const { classes } = useContentStyles();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  return (
    <div ref={wrapperRef} className={classes.contentWrapper}>
      <Box component="main" ref={mainRef} className={classes.content} sx={sx}>
        {children}
      </Box>
      <MainLayoutScrollbars scrollRef={mainRef} wrapperRef={wrapperRef} />
    </div>
  );
};

type SidePanelElement = ReactElement<
  ComponentProps<typeof SidePanel>,
  typeof SidePanel
>;

type MainLayoutProps = {
  children: React.ReactNode;
  panelRight?: SidePanelElement | null;
  panelLeft?: SidePanelElement | null;
  /**
   * Pass `'none'` on routes that render no AppBar (e.g. the PDF print view),
   * so the layout fills the viewport instead of leaving an empty band on top.
   */
  appBarSpace?: AppBarSpace;
};

/**
 * A layout component that is used to display content under the AppBar (header) and show optional sidepanels on the left and right.
 * @param children - The children to display in the main layout.
 * @returns
 */
const MainLayoutComponent: React.FC<MainLayoutProps> = ({
  children,
  panelRight = null,
  panelLeft = null,
  appBarSpace = 'offset',
}) => {
  const { classes } = useStyles({ appBarSpace });
  // Side panels get auto width which make them use their own width. Conent should be resopsive (1fr)
  const gridTemplateColumns = useMemo(() => {
    return [
      panelLeft ? 'auto' : undefined,
      '1fr',
      panelRight ? 'auto' : undefined,
    ]
      .filter(guards.is.defined)
      .join(' ');
  }, [panelRight, panelLeft]);

  return (
    <Box
      className={classes.root}
      sx={{
        gridTemplateColumns: gridTemplateColumns,
      }}
    >
      {panelLeft && <div className={classes.sidePanelLeft}>{panelLeft}</div>}
      {children}
      {panelRight && <div className={classes.sidePanelRight}>{panelRight}</div>}
    </Box>
  );
};

/**
 * A layout component that is used to display content under the AppBar (header) and show optional sidepanels on the left and right.
 */
export const MainLayout = Object.assign(MainLayoutComponent, {
  Content: MainContent,
  SidePanel,
});
